import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../mobile.css';

const MobileResetPassword = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState(location.state?.email || '');
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isCodeLoading, setIsCodeLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [successPopup, setSuccessPopup] = useState(false);

  const passwordMismatch = newPassword && confirmPassword && newPassword !== confirmPassword;
  const resetDisabled = loading || !email || !newPassword || !confirmPassword || newPassword !== confirmPassword || !verificationCode;
  const sendDisabled = isCodeLoading || !email;

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const sendCode = async () => {
    setErrorMessage('');
    setIsCodeLoading(true);

    try {
      const res = await fetch(`${API_URL}/user/verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        setErrorMessage(res.status >= 500 ? 'Server error. Please try again later.' : 'Mobile not registered. Please check and try again.');
        return;
      }

      setVerificationCodeSent(true);
      setResendTimer(90);
    } catch {
      setErrorMessage('An error occurred. Please try after some time.');
      setVerificationCodeSent(false);
    } finally {
      setIsCodeLoading(false);
    }
  };

  const handleReset = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/user/reset`, { email, password: newPassword, verificationCode });
      setSuccessPopup(true);
    } catch {
      setErrorMessage('Server error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px' };

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: 600, textAlign: 'center', marginBottom: '20px' }}>Reset Password</h1>

      <input
        placeholder="Registered Mobile Number"
        value={email}
        inputMode="numeric"
        onChange={(e) => setEmail((e.target.value || '').replace(/\D/g, '').slice(0, 10))}
        style={inputStyle}
      />

      {verificationCodeSent && (
        <input
          placeholder="OTP"
          value={verificationCode}
          inputMode="numeric"
          onChange={(e) => setVerificationCode(e.target.value)}
          style={inputStyle}
        />
      )}

      {verificationCodeSent && (
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={{ ...inputStyle, marginBottom: 0, paddingRight: '40px' }}
          />
          <span onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#666' }}>
            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
          </span>
        </div>
      )}

      {verificationCodeSent && (
        <div style={{ position: 'relative', margin: '12px 0' }}>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{ ...inputStyle, marginBottom: 0, paddingRight: '40px' }}
          />
          <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#666' }}>
            {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
          </span>
        </div>
      )}

      {passwordMismatch && <p style={{ color: 'red' }}>Passwords do not match.</p>}

      {verificationCodeSent ? (
        <button onClick={handleReset} disabled={resetDisabled} className="m-button-primary">
          {loading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Reset Password'}
        </button>
      ) : (
        <button onClick={sendCode} disabled={sendDisabled} className="m-button-primary">
          {isCodeLoading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Send OTP'}
        </button>
      )}

      {verificationCodeSent && resendTimer > 0 && (
        <p style={{ color: '#094268', textAlign: 'center', marginTop: '20px' }}>OTP sent to your mobile. Resend in {resendTimer}s.</p>
      )}

      {verificationCodeSent && resendTimer === 0 && (
        <p style={{ textAlign: 'center' }}>
          <button onClick={sendCode} disabled={isCodeLoading} className="m-pill-btn" style={{ color: '#094268' }}>Resend OTP</button>
        </p>
      )}

      <p style={{ textAlign: 'center', marginTop: '10px' }}>
        <button onClick={() => navigate('/m/login', { state: { email } })} className="m-pill-btn" style={{ color: '#094268' }}>Cancel</button>
      </p>

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}

      {successPopup && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Success</h3>
            <p>Password reset successful. Please log in.</p>
            <button className="m-button-primary" onClick={() => navigate('/m/login')}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileResetPassword;
